import { Drawer } from './index';
import Button from '../common/Button';
import { useToast } from '../../context/ToastContext';
import './UserDrawer.css';

const MOCK_USER = {
  email: 'john@acme.com',
  phone: '+15551234567',
  phoneVerified: true,
  emailVerified: true,
  created: 'Nov 15, 2024',
  lastAuth: '2 mins ago',
  fraudScore: 12,
  status: 'Active',
  tenants: [
    { name: 'Cachee', role: 'Admin', joined: 'Nov 15, 2024', primary: true }
  ],
  sessions: [
    { id: 1, device: 'Chrome on MacOS', ip: '192.168.1.100', location: 'New York, US', created: 'Dec 9, 2024 10:30 AM', lastActive: '2 mins ago' },
    { id: 2, device: 'Safari on iPhone', ip: '192.168.1.101', location: 'New York, US', created: 'Dec 8, 2024 3:15 PM', lastActive: '1 hour ago' },
    { id: 3, device: 'Auth1 Mobile App', ip: '10.0.0.50', location: 'Brooklyn, US', created: 'Dec 1, 2024 9:00 AM', lastActive: '3 days ago' },
  ],
  authHistory: [
    { time: '10:32 AM', event: 'Login via SMS OTP (Chrome/MacOS)', status: 'success' },
    { time: 'Yesterday', event: 'Login via Magic Link (Safari/iOS)', status: 'success' },
    { time: 'Dec 7', event: 'Failed OTP attempt (wrong code)', status: 'warning' },
    { time: 'Dec 5', event: 'Login via SMS OTP (Chrome/MacOS)', status: 'success' },
    { time: 'Dec 1', event: 'New device registered (iPhone)', status: 'info' },
  ],
  tags: ['VIP Customer', 'Verified Business'],
};

const UserDrawer = ({ isOpen, onClose, userEmail }) => {
  const { showToast } = useToast();
  const user = { ...MOCK_USER, email: userEmail || MOCK_USER.email };

  const handleRevokeSession = (sessionId) => {
    showToast(`Session ${sessionId} revoked`, 'success');
  };

  const handleRevokeAllSessions = () => {
    showToast('All sessions revoked', 'success');
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="User Details" width="420px">
      <div className="user-drawer-content">
        <div className="user-email-display">{user.email}</div>

        {/* Profile Section */}
        <div className="drawer-section">
          <h4 className="drawer-section-title">👤 Profile</h4>
          <div className="user-info-grid">
            <div className="user-info-item">
              <span className="user-info-label">Phone</span>
              <span className="user-info-value">{user.phone} {user.phoneVerified && '📱✓'}</span>
            </div>
            <div className="user-info-item">
              <span className="user-info-label">Email</span>
              <span className="user-info-value">{user.email} {user.emailVerified && '📧✓'}</span>
            </div>
            <div className="user-info-item">
              <span className="user-info-label">Created</span>
              <span className="user-info-value">{user.created}</span>
            </div>
            <div className="user-info-item">
              <span className="user-info-label">Last Auth</span>
              <span className="user-info-value">{user.lastAuth}</span>
            </div>
            <div className="user-info-item">
              <span className="user-info-label">Fraud Score</span>
              <span className="user-info-value text-success">{user.fraudScore} (Low Risk)</span>
            </div>
            <div className="user-info-item">
              <span className="user-info-label">Status</span>
              <span className="user-info-value">
                <span className="badge badge-success">{user.status}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Tenant Memberships */}
        <div className="drawer-section">
          <h4 className="drawer-section-title">🏢 Tenant Memberships</h4>
          {user.tenants.map((tenant, idx) => (
            <div key={idx} className="session-item">
              <div className="session-item-header">
                <span className="session-device">{tenant.name}</span>
                <span className="badge badge-info">{tenant.role}</span>
              </div>
              <div className="session-meta">{tenant.primary && 'Primary tenant • '}Joined {tenant.joined}</div>
              <div className="session-actions">
                <Button variant="secondary" size="xs" onClick={() => showToast('Opening role editor...', 'info')}>
                  Change Role
                </Button>
                <Button variant="secondary" size="xs" onClick={() => showToast('Remove confirmation needed', 'warning')}>
                  Remove
                </Button>
              </div>
            </div>
          ))}
          <Button variant="secondary" size="sm" icon="plus" className="full-width">
            Add to Another Tenant
          </Button>
        </div>

        {/* Active Sessions */}
        <div className="drawer-section">
          <h4 className="drawer-section-title">🔐 Active Sessions ({user.sessions.length})</h4>
          {user.sessions.map((session) => (
            <div key={session.id} className="session-item">
              <div className="session-item-header">
                <span className="session-device">{session.device}</span>
                <Button variant="danger" size="xs" onClick={() => handleRevokeSession(session.id)}>
                  Revoke
                </Button>
              </div>
              <div className="session-meta">
                <div>IP: {session.ip} • {session.location}</div>
                <div>Created: {session.created} • Last active: {session.lastActive}</div>
              </div>
            </div>
          ))}
          <Button variant="danger" size="sm" className="full-width" onClick={handleRevokeAllSessions}>
            🚫 Revoke All Sessions
          </Button>
        </div>

        {/* Auth History */}
        <div className="drawer-section">
          <h4 className="drawer-section-title">📜 Auth History</h4>
          {user.authHistory.map((item, idx) => (
            <div key={idx} className="timeline-item">
              <span className="timeline-time">{item.time}</span>
              <span className={`timeline-event ${item.status}`}>
                {item.status === 'success' && '✅'}
                {item.status === 'warning' && '⚠️'}
                {item.status === 'info' && '📱'}
                {' '}{item.event}
              </span>
            </div>
          ))}
          <Button variant="secondary" size="sm" className="full-width">
            View Full History
          </Button>
        </div>

        {/* Fraud Signals */}
        <div className="drawer-section">
          <h4 className="drawer-section-title">🛡️ Fraud Signals</h4>
          <div className="fraud-signal-box low-risk">
            <div className="fraud-signal-title">✓ Low Risk (Score: {user.fraudScore})</div>
            <div className="fraud-signal-details">
              • No VOIP numbers detected<br />
              • Consistent location (New York area)<br />
              • No velocity violations<br />
              • Known device fingerprints
            </div>
          </div>
          <div className="fraud-actions">
            <Button variant="secondary" size="sm" icon="eye">Add to Watchlist</Button>
            <Button variant="secondary" size="sm">🧹 Clear Score</Button>
          </div>
        </div>

        {/* Notes & Tags */}
        <div className="drawer-section">
          <h4 className="drawer-section-title">📝 Notes & Tags</h4>
          <div className="user-tags">
            {user.tags.map((tag, idx) => (
              <span key={idx} className="badge badge-info">{tag}</span>
            ))}
          </div>
          <textarea className="form-input" rows={3} placeholder="Add a note about this user..." />
          <Button variant="secondary" size="sm">Save Note</Button>
        </div>
      </div>
    </Drawer>
  );
};

export default UserDrawer;
